/**
 * <restock-notify> — captures an email address and posts it to the
 * back-in-stock subscribe endpoint.
 *
 * Reads configuration from data attributes set in snippets/restock-notify.liquid:
 *   data-endpoint      Subscribe URL
 *   data-product-gid   gid://shopify/Product/<id>
 *   data-text-success  Message shown on success
 *   data-text-error    Message shown on failure
 *
 * The submit listener is bound to the host element rather than the inner form,
 * so it survives Horizon re-rendering the product section on variant change.
 */
class RestockNotify extends HTMLElement {
  #bound = false;

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;
    this.addEventListener('submit', this.#handleSubmit);
  }

  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.target.closest('form');
    if (!form) return;

    const input = form.querySelector('input[type="email"]');
    const button = form.querySelector('button[type="submit"]');
    const { endpoint, productGid } = this.dataset;

    if (!input || !endpoint || !productGid) return;

    const email = input.value.trim();

    if (!email || !input.checkValidity()) {
      input.reportValidity();
      return;
    }

    if (button) button.disabled = true;
    this.#setStatus('', null);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_gid: productGid, email }),
      });

      if (!response.ok) {
        throw new Error(`Subscribe failed with status ${response.status}`);
      }

      form.hidden = true;
      this.#setStatus(this.dataset.textSuccess, 'success');
    } catch (error) {
      console.error('[restock-notify]', error);
      if (button) button.disabled = false;
      this.#setStatus(this.dataset.textError, 'error');
    }
  };

  #setStatus(message, state) {
    const node = this.querySelector('[data-restock-status]');
    if (!node) return;

    node.textContent = message ?? '';
    node.hidden = !message;

    if (state) {
      node.dataset.state = state;
    } else {
      delete node.dataset.state;
    }
  }
}

if (!customElements.get('restock-notify')) {
  customElements.define('restock-notify', RestockNotify);
}
